import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import enTranslations from '../../i18n/locales/en.json';
import plTranslations from '../../i18n/locales/pl.json';

type ItineraryPreviewMeta = {
  title: string;
  titleWithDuration: string;
  titleDefault: string;
  description: string;
  descriptionWithDuration: string;
  descriptionDefault: string;
};

type TranslationsWithMeta = {
  meta: {
    itineraryPreview: ItineraryPreviewMeta;
  };
};

const translations: Record<string, TranslationsWithMeta> = {
  en: enTranslations,
  pl: plTranslations,
};

const API_BASE_URL =
  typeof process !== 'undefined'
    ? process.env.VITE_API_BASE_URL || 'https://api.hopsa.app'
    : import.meta.env.VITE_API_BASE_URL || 'https://api.hopsa.app';

const MEDIA_STORAGE_URL =
  typeof process !== 'undefined'
    ? process.env.VITE_MEDIA_STORAGE_URL || ''
    : import.meta.env.VITE_MEDIA_STORAGE_URL || '';

const APP_URL =
  typeof process !== 'undefined'
    ? process.env.VITE_APP_URL || 'https://planner.hopsa.app'
    : import.meta.env.VITE_APP_URL || 'https://planner.hopsa.app';

interface TripMetadata {
  title: string;
  description: string;
  ogImage: string | undefined;
  destinationName: string;
  country: string;
  duration: number;
}

async function fetchTripMetadata(
  tripId: string,
  locale: string,
): Promise<TripMetadata> {
  const t = translations[locale] || translations.en;
  const meta = t.meta.itineraryPreview;

  const defaultMeta: TripMetadata = {
    title: meta.titleDefault,
    description: meta.descriptionDefault,
    ogImage: MEDIA_STORAGE_URL
      ? `${MEDIA_STORAGE_URL}/media/planner/uploads/${tripId}.jpg`
      : undefined,
    destinationName: '',
    country: '',
    duration: 0,
  };

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(
      `${API_BASE_URL}/api/v1/planner/plans/preview/${tripId}`,
      { signal: controller.signal },
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      return defaultMeta;
    }

    const parsed = (await response.json()) as unknown;
    const tripData =
      typeof parsed === 'object' && parsed !== null && 'data' in parsed
        ? (parsed as { data: unknown }).data
        : parsed;
    const tripDataRecord =
      typeof tripData === 'object' && tripData !== null
        ? (tripData as Record<string, unknown>)
        : {};

    // Extract destination
    const preferences = tripDataRecord.preferences as
      | Record<string, unknown>
      | undefined;
    const dest = preferences?.destination ?? tripDataRecord.destination;
    let destinationName = 'Trip';
    let country = '';

    if (typeof dest === 'string') {
      destinationName = dest;
      country = dest.split(',').pop()?.trim() || '';
    } else if (dest && typeof dest === 'object') {
      const destRecord = dest as Record<string, unknown>;
      destinationName = (destRecord.name as string) || 'Trip';
      country = (destRecord.country as string) || '';
    }

    // Calculate duration
    let duration = 0;
    if (tripDataRecord.start_date && tripDataRecord.end_date) {
      const start = new Date(String(tripDataRecord.start_date));
      const end = new Date(String(tripDataRecord.end_date));
      duration =
        Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) +
        1;
    } else if (
      typeof tripDataRecord.itinerary === 'object' &&
      tripDataRecord.itinerary !== null &&
      Array.isArray((tripDataRecord.itinerary as Record<string, unknown>).days)
    ) {
      duration = (
        (tripDataRecord.itinerary as Record<string, unknown>).days as unknown[]
      ).length;
    }

    const t = translations[locale] || translations.en;
    const metaT = t.meta.itineraryPreview;
    const locationText =
      country && country !== destinationName
        ? `${destinationName}, ${country}`
        : destinationName;

    const title =
      duration > 0
        ? metaT.titleWithDuration
            .replace('{{duration}}', String(duration))
            .replace('{{destination}}', locationText)
        : metaT.title.replace('{{destination}}', locationText);

    const description =
      duration > 0
        ? metaT.descriptionWithDuration
            .replace('{{duration}}', String(duration))
            .replace('{{destination}}', locationText)
        : metaT.description.replace('{{destination}}', locationText);

    return {
      title,
      description,
      ogImage: defaultMeta.ogImage,
      destinationName,
      country,
      duration,
    };
  } catch (error) {
    // Silently handle network errors in SSR - client will retry if needed
    if (process.env.NODE_ENV === 'development') {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.warn(
        `[SSR] Trip metadata fetch skipped (tripId: ${tripId}): ${errorMessage}`,
      );
    }
    return defaultMeta;
  }
}

const searchSchema = z
  .object({
    tripId: z.string().optional(),
    action: z.string().optional(),
    anonymous: z.union([z.string(), z.boolean()]).optional(),
    checkout: z.string().optional(),
    returnView: z.string().optional(),
  })
  .passthrough();

export const Route = createFileRoute('/$locale/itinerary-preview')({
  validateSearch: searchSchema,
  ssr: true,
  loaderDeps: ({ search }) => ({ tripId: search.tripId }),
  loader: async ({ deps, params }) => {
    const tripId = deps.tripId;
    const locale = params.locale || 'en';
    const t = translations[locale] || translations.en;
    const metaT = t.meta.itineraryPreview;

    if (!tripId) {
      return {
        meta: {
          title: metaT.titleDefault,
          description: metaT.descriptionDefault,
          ogImage: undefined,
          destinationName: '',
          country: '',
          duration: 0,
        },
      };
    }

    const meta = await fetchTripMetadata(tripId, locale);
    return { meta, tripId };
  },
  head: ({ loaderData, params }) => {
    const meta = loaderData?.meta;
    const locale = params.locale || 'en';
    const tripId = loaderData?.tripId;
    const fullUrl = tripId
      ? `${APP_URL}/${locale}/itinerary-preview?tripId=${tripId}`
      : `${APP_URL}/${locale}/itinerary-preview`;

    if (!meta) {
      const t = translations[locale] || translations.en;
      const metaT = t.meta.itineraryPreview;
      return {
        meta: [
          { title: metaT.titleDefault },
          {
            name: 'description',
            content: metaT.descriptionDefault,
          },
        ],
      };
    }

    const metaTags = [
      { name: 'description', content: meta.description },
      // Open Graph
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: fullUrl },
      { property: 'og:title', content: meta.title },
      { property: 'og:description', content: meta.description },
      { property: 'og:site_name', content: 'Hopsa' },
      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:url', content: fullUrl },
      { name: 'twitter:title', content: meta.title },
      { name: 'twitter:description', content: meta.description },
    ];

    // Add image meta tags if available
    if (meta.ogImage) {
      const imageAlt = `${meta.destinationName} - ${meta.duration} day trip itinerary`;
      metaTags.push(
        { property: 'og:image', content: meta.ogImage },
        { property: 'og:image:secure_url', content: meta.ogImage },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:type', content: 'image/jpeg' },
        { property: 'og:image:alt', content: imageAlt },
        { name: 'twitter:image', content: meta.ogImage },
        { name: 'twitter:image:alt', content: imageAlt },
      );
    }

    return {
      meta: metaTags,
      title: meta.title,
      links: [{ rel: 'canonical', href: fullUrl }],
    };
  },
});
